import type { StyleResolver } from './style-resolver.model';
import {
  AngleStyleResolver,
  CircleStyleResolver,
  CurveStyleResolver,
  LabelStyleResolver,
  LineStyleResolver,
  PointStyleResolver,
  PolygonStyleResolver,
} from './style-resolvers';

/**
 * Registry of style resolvers.
 *
 * Built-in resolvers are registered automatically.
 * Widgets can register additional resolvers for custom element types.
 */
export class StyleResolverRegistry {
  private static readonly resolvers = new Map<string, StyleResolver>();
  private static initialized = false;

  private static initialize(): void {
    if (this.initialized) {
      return;
    }

    // Register built-in resolvers
    this.register(new CurveStyleResolver());
    this.register(new PointStyleResolver());
    this.register(new LineStyleResolver());
    this.register(new PolygonStyleResolver());
    this.register(new LabelStyleResolver());
    this.register(new AngleStyleResolver());
    this.register(new CircleStyleResolver());

    this.initialized = true;
  }

  /**
   * Register a style resolver.
   * Can be used by widgets to add custom resolvers.
   */
  static register(resolver: StyleResolver): void {
    this.resolvers.set(resolver.key, resolver);
  }

  /**
   * Get a resolver by key.
   */
  static get(key: string): StyleResolver | undefined {
    this.initialize();
    return this.resolvers.get(key);
  }

  /**
   * Get all registered resolvers.
   */
  static getAll(): StyleResolver[] {
    this.initialize();
    return Array.from(this.resolvers.values());
  }

  /**
   * Check if a resolver exists for a key.
   */
  static has(key: string): boolean {
    this.initialize();
    return this.resolvers.has(key);
  }
}
